import { Button, Space } from "antd";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { AttrSelectTableLayout } from "src/business/attribute/attrEdits/components/AttrSelectTableLayout";
import { ClassesFilter, ZClassRow } from "src/business/classes";
import { classesLoader2, loadClsBranch } from "src/business/classes/apiClasses";
import { buildClassColumns } from "src/business/classes/buildClassColumns";
import {
  ClassesTree,
  ClassesTreeStore,
} from "src/business/classes/ClassesTree";
import { loadClassifier, ZClassifier } from "src/business/classifier";
import { loadSchemes, ZSpecification } from "src/business/specification";
import { makeHierarchicalLabel } from "src/business/sysData/SysParentsColumn";
import { onError } from "src/common/onError";
import { makeSidLabelsFromKeys, optionsMaker } from "src/common/optionsMaker";
import { RemoteData } from "src/common/RemoteData";
import { LoaderBox } from "src/components/LoaderBox";
import { MdmDrawer } from "src/components/MdmDrawer";
import { QuerySearch } from "src/components/QuerySearch/QuerySearch";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { RtKey } from "src/components/ReactiveTable/RtTypes";
import { ZOption, ZSidLabel } from "src/types";

// Некоторые данные (спецификации для классов) имеет смысл загружать только в случае открытия модалки
// Для этого используется стор

type Data = {
  classifier: ZClassifier;
  schemes: ZSpecification[];
  table: RtStore<ZClassRow, ClassesFilter>;
  columns: RtColumn<ZClassRow>[];
  tree: ClassesTreeStore;
};

type PropsClassesSelectModal = {
  open: boolean;
  close: () => void;
  classifier: ZSidLabel;
  value: ZSidLabel[];
  initialParents?: Record<string, ZSidLabel[][]>;
  onChange?: (newValues: ZSidLabel[]) => void;
};

export const ClassesSelectModal: React.FC<PropsClassesSelectModal> = observer(
  (props) => {
    const { open, close, classifier, value, initialParents, onChange } = props;
    const store = React.useMemo(
      () =>
        open
          ? new ClassesSelectModalStore(classifier.id, value, initialParents)
          : null,
      [open],
    );
    const onOk = () => {
      if (store) onChange?.(store.finalValue);
      close();
    };
    return (
      <MdmDrawer
        open={open}
        onClose={close}
        title={classifier.label}
        wide
        extra={
          <Space>
            <Button onClick={close}>Отменить</Button>
            <Button
              type="primary"
              disabled={!(store?.data.status === "ready")}
              onClick={onOk}
            >
              Выбрать
            </Button>
          </Space>
        }
      >
        {!!store && (
          <LoaderBox
            remoteData={store.data}
            drawReady={(data) => <Inner data={data} store={store} />}
          />
        )}
      </MdmDrawer>
    );
  },
);

type PropsInner = {
  data: Data;
  store: ClassesSelectModalStore;
};

const Inner: React.FC<PropsInner> = observer(({ data, store }) => {
  const { table, columns, tree } = data;
  const onTree = (parentId: string | undefined) => {
    table.changeSingleFilter("parentId", parentId);
  };
  const onTag = () => {
    table.changeSingleFilter("parentId", undefined);
  };
  const curParentId = table.filters?.parentId;
  React.useEffect(() => {
    const node = data.tree.findRootNodeById(curParentId ?? "");
    data.tree.setActiveNodeKey(node?.key ?? null);
  }, [curParentId]);

  return (
    <AttrSelectTableLayout
      table={table}
      columns={columns}
      makeOption={store.makeOption}
      leftPart={<ClassesTree treeStore={tree} onChangeNode={onTree} />}
      activeFilterLabel={tree.activeNode?.name}
      onClearFilter={onTag}
      mainTools={
        <QuerySearch
          value={table.filters?.query}
          onChange={(value) => table.changeSingleFilter("query", value)}
        />
      }
    />
  );
});

class ClassesSelectModalStore {
  constructor(
    classifierId: string,
    private readonly srcValue: ZSidLabel[],
    initialParents?: Record<string, ZSidLabel[][]>,
  ) {
    /** srcValue при передаче в optionsMaker сразу кэшируется, поэтому строим нужный лейбл до */
    const srcValueWithParentLabels = srcValue.map((v) => ({
      id: v.id,
      label: makeHierarchicalLabel(initialParents?.[v.id], v.label),
    }));
    this.makeOption = optionsMaker(srcValueWithParentLabels, (row) =>
      makeHierarchicalLabel(row.parents, row.name),
    );
    makeAutoObservable(this);
    this.init(classifierId).catch(onError);
  }

  makeOption: (key: RtKey, row?: ZClassRow) => ZOption;

  get finalValue(): ZSidLabel[] {
    const { data } = this;
    if (data.status !== "ready") return [];
    const res = makeSidLabelsFromKeys(
      data.result.table.selectedKeys,
      this.makeOption,
    );
    return res;
  }

  data: RemoteData<Data> = { status: "none" };

  setData(newData: RemoteData<Data>) {
    this.data = newData;
  }

  async init(classifierId: string) {
    try {
      this.setData({ status: "wait" });
      const classifier = await loadClassifier(classifierId);
      const schemes = await loadSchemes(classifierId);
      const root = await loadClsBranch(classifierId);
      const columns = buildClassColumns(schemes, classifier.displayAttribute);
      const table = new RtStore<ZClassRow, ClassesFilter>({
        rowKey: "id",
        loader: classesLoader2(classifierId, schemes),
        selectionType: "checkbox",
        keepSelected: true,
        defaultRequest: { pageSize: 20 },
      });
      table.setSelectedKeys(this.srcValue.map(({ id }) => id));
      const tree = new ClassesTreeStore(root);
      this.setData({
        status: "ready",
        result: { classifier, schemes, columns, table, tree },
      });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }
}
